import { createRoute, useLoaderData } from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import { unwrapPageInitialData, type PageInitialData } from "~/app/api/pageData";
import { newContractPageQueryOptions } from "~/app/api/pageData/newContractPageQuery";
import type { NewContractPageInitialData } from "~/app/api/pageData/newContractPageInitialData";
import { StorefrontShellFrame } from "~/app/shell/StorefrontShellFrame";
import { useStorefrontShellProps } from "~/app/router/storefrontShellPropsContext";
import { rootRoute } from "~/app/router/rootRoute";
import {
  createRequestContractPage as createNewContractPageComponent,
  type Props as NewContractPageProps,
} from "~/pages/RequestContractPage/RequestContractPage";
import type { B2BUnitDataDTO } from "~/features/cart/cart/dto/CartResponseDTO";

const NEW_CONTRACT_PATH = "/my-account/new-contract";

const EMPTY_SOLD_TO: B2BUnitDataDTO = { uid: "", name: "", addresses: [], addAddress: false };

/**
 * Reproduces the legacy controller-built {@code reactPageProps} shape that fed the
 * {@code CreateContractPageComponent} island via {@code createComponentAtId}: the backend
 * {@code approvedAddresses}/{@code currentSoldTo} body fields are forwarded to the island under
 * the legacy prop names {@code addresses}/{@code soldTo}. No renaming is needed beyond the field
 * name swap — the serialized {@code AddressData}/{@code B2BUnitData} shapes already match the
 * island's {@code AddressDTO}/{@code B2BUnitDataDTO} contracts. The empty-state fallbacks only
 * apply when the body is absent, which does not occur on the OK path the loader unwraps.
 */
function toNewContractProps(data: NewContractPageInitialData | undefined): NewContractPageProps {
  return {
    addresses: data?.approvedAddresses ?? [],
    soldTo: data?.currentSoldTo ?? EMPTY_SOLD_TO,
  };
}

function NewContractRoute() {
  const result = useLoaderData({
    from: NEW_CONTRACT_PATH,
  }) as PageInitialData<NewContractPageInitialData>;
  const shellProps = useStorefrontShellProps();

  return (
    <StorefrontShellFrame variant={shellProps.variant} metadata={result.metadata}>
      {createNewContractPageComponent(toNewContractProps(result.data))}
    </StorefrontShellFrame>
  );
}

export async function loadNewContractPageData(context: { queryClient: QueryClient }) {
  const result = await context.queryClient.ensureQueryData(newContractPageQueryOptions());

  return unwrapPageInitialData(result);
}

export const newContractRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: NEW_CONTRACT_PATH,
  loader: async ({ context }) => loadNewContractPageData(context),
  component: NewContractRoute,
});
